import React, { forwardRef } from 'react';
import { getConfiguration } from '../utils/configuration';
import './MyBetsShareCard.css';
import { formatDate } from '../utils/formatDate';
import { systemCode } from '../..';
import { getLabel } from '../labels';
import { getCurrencySymbol } from '../utils/getCurrencySymbol';
import { getShareCardConfig } from './MyBetsShareCardConfig';
import CheckBetSnapshotUI from './my-bets-layout-3/checkbet-layout-3/CheckBetSnapshotUI';
import { getTicketCategoryLabel } from '../utils/getTicketCategorylabel';
import { getStatusBadgeMod } from '../utils/getStatusBadgeMod';
import { getEnv } from '../env';

const MyBetsShareCard = forwardRef((
    {
        totalAmount, totalOdd, winPot, bonusAmount,
        ticketTypeLabel,
        ticketId, dateTimestamp, status, isSnapshot,
        hideAmounts, hideStake, hideWinnings,
        selectedBackground,
        logoBase64,
        SnapshotComponent,
    },
    ref
) => {
    const domain = getEnv('REACT_APP_API_DOMAIN')
    const conf = getConfiguration();
    const cardConfig = getShareCardConfig();
    const imgBasePath = `${domain}/external_css`;
    const logoUrl = `/external_css/${systemCode}/img/share/logo.png`;
    // const logoUrl = `${imgBasePath}/${systemCode}/img/logo.png`;
    const resolvedLogoSrc = logoBase64 || logoUrl;

    if (!conf.sdt) return null;

    const EventsRenderer = SnapshotComponent || CheckBetSnapshotUI;
    const shouldHideStake = hideAmounts || hideStake;
    const shouldHideWinnings = hideAmounts || hideWinnings;
    const winStatuses = ['winning', 'win', 'paid', 'to_be_paid', 'winning_cashback'];
    const loseStatuses = ['losing', 'lose', 'lost'];
    const isWinning = winStatuses.includes(status);
    const isLosing = loseStatuses.includes(status);

    const formattedDate = dateTimestamp
        ? formatDate(dateTimestamp).formatted
        : new Date().toLocaleDateString();

    const getStatusImg = () => {
        if (isWinning) return 'img-win';
        if (isLosing) return 'img-lost';
        return 'img-pending';
    };

    const getStatusStyle = () => {
        if (isWinning) return { bg: '#28a745', text: getLabel('ticket.winning') };
        if (isLosing) return { bg: '#dc3545', text: getLabel('ticket.losing') };
        return { bg: '#b71c1c', text: getLabel('general.ongoing') };
    };

    const statusConfig = getStatusStyle();
    const statusImg = getStatusImg();
    const currency = getCurrencySymbol(conf.cs.ct);
    const ticketCategoryLabel = getTicketCategoryLabel(status);
    const hideImg = (e) => { e.target.style.display = 'none'; };
    const hasCustomBg = !!selectedBackground;

    const containerClass = [
        'mb-share-card',
        isWinning ? 'won' : 'lost',
        isSnapshot ? 'snapshot-mode' : '',
        hasCustomBg ? 'mb-share-card--custom-bg' : '',
        cardConfig.containerClass,
    ].filter(Boolean).join(' ');

    const containerStyle = {};
    if (selectedBackground) {
        containerStyle.backgroundColor = selectedBackground.color || cardConfig.backgroundColor;
        containerStyle.backgroundImage = 'none';
    }

    const StatsGrid = ({ className }) => (
        <div className={`mb-share-stats-grid${className ? ` ${className}` : ''}`}>
            {!shouldHideStake && (
                <div className="mb-share-stat-box">
                    <span className="mb-share-label">{getLabel('mybets.stake', 'Puntata')}</span>
                    <span className="mb-share-value">{totalAmount}</span>
                </div>
            )}

            {bonusAmount && !shouldHideWinnings && !shouldHideStake && (
                <div className="mb-share-stat-box">
                    <span className="mb-share-label">{getLabel('general.bonus', 'Bonus')}</span>
                    <span className="mb-share-value">{bonusAmount}</span>
                </div>
            )}
            <div className="mb-share-stat-box">
                <span className="mb-share-label">{getLabel('mybets.quotatotale', 'Quota totale')}</span>
                <span className="mb-share-value">{totalOdd}</span>
            </div>
            {!shouldHideWinnings && (
                <div className="mb-share-stat-box mb-share-win-box">
                    <span className="mb-share-label">
                        {isWinning
                            ? getLabel('mybets.vincita', 'Vincita')
                            : getLabel('betslip.potential_winnings', 'Vincita Pot.')}
                    </span>
                    <span className="mb-share-value">{winPot} {currency}</span>
                </div>
            )}
        </div>
    );

    return (
        <div ref={ref} className={containerClass} style={containerStyle}>

            {selectedBackground?.imageUrl && (
                <img
                    src={selectedBackground.imageUrl}
                    alt=""
                    aria-hidden="true"
                    crossOrigin="anonymous"
                    onError={hideImg}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        zIndex: 0,
                        pointerEvents: 'none',
                    }}
                />
            )}

            <div style={{ position: 'relative', zIndex: 1 }}>

                {cardConfig.showStatusImage && (
                    <div className="mb-share-big-check">
                        <img
                            src={`${imgBasePath}/${systemCode}/img/share/${statusImg}.svg`}
                            alt="status"
                            className="mb-share-status-img"
                            crossOrigin="anonymous"
                            onError={hideImg}
                        />
                    </div>
                )}

                {cardConfig.showTicketHeader ? (
                    <>
                        {cardConfig.showLogoHeader && (
                            <div className="mb-share-logo-row">
                                <img
                                    src={resolvedLogoSrc}
                                    alt="Logo"
                                    className="mb-share-logo-img"
                                    crossOrigin="anonymous"
                                    onError={hideImg}
                                />
                            </div>
                        )}

                        <div className="mb-share-white-card">
                            <div className="mb-share-ticket-header">
                                <span className={`sc__status-badge mb-share-ticket-badge ${getStatusBadgeMod(status)}`}>
                                    {ticketCategoryLabel}
                                </span>
                                {ticketTypeLabel && (
                                    <span className="mb-share-ticket-type">
                                        {ticketTypeLabel.toUpperCase()}
                                    </span>
                                )}
                                <span className="mb-share-ticket-date">
                                    {formattedDate}
                                </span>
                            </div>

                            <div className="mb-share-content mb-share-content--in-card">
                                <EventsRenderer
                                    ticketId={ticketId}
                                    isSnapshot={isSnapshot}
                                    hideAmounts={shouldHideStake && shouldHideWinnings}
                                />
                            </div>

                            {cardConfig.showBottomStats && (
                                <StatsGrid className="mb-share-stats-grid--bottom" />
                            )}
                        </div>
                    </>
                ) : (
                    <>
                        {cardConfig.showLogoHeader && (
                            <div className="mb-share-header-row">
                                <div className="mb-share-header-center">
                                    <img
                                        src={resolvedLogoSrc}
                                        alt="Logo"
                                        className="mb-share-logo-img"
                                        crossOrigin="anonymous"
                                        onError={hideImg}
                                    />
                                    {cardConfig.showDate && (
                                        <div className="mb-share-played-date">Giocato il {formattedDate}</div>
                                    )}
                                </div>
                                {cardConfig.showDomain && (
                                    <div className="mb-share-header-right">{window.location.hostname}</div>
                                )}
                            </div>
                        )}

                        {cardConfig.showInfoTkt && <StatsGrid />}

                        {cardConfig.showStatusBar && (
                            <div className="mb-share-status-bar" style={{ backgroundColor: statusConfig.bg }}>
                                {statusConfig.text}
                            </div>
                        )}

                        <div className="mb-share-content">
                            <EventsRenderer
                                ticketId={ticketId}
                                isSnapshot={isSnapshot}
                                hideAmounts={shouldHideStake && shouldHideWinnings}
                            />
                        </div>

                        {cardConfig.showBottomStats && (
                            <StatsGrid className="mb-share-stats-grid--bottom" />
                        )}

                        {cardConfig.showFooter && (
                            <div className="mb-share-footer">
                                {cardConfig.showFooterLogo && (
                                    <div className="mb-share-footer-db-logo">
                                        <img
                                            src={`${imgBasePath}/${systemCode}/img/share/logo_footer.png`}
                                            alt="Logo"
                                            style={{ height: '40px' }}
                                            crossOrigin="anonymous"
                                            onError={hideImg}
                                        />
                                    </div>
                                )}
                                {cardConfig.showFooterLegal && (
                                    <div className="mb-share-legal-text">
                                        {getLabel('checkbet.footer_share_ticket')} {getLabel('checkbet.footer_concessione')}
                                    </div>
                                )}
                                {cardConfig.showFooterIcons && (
                                    <div className="mb-share-footer-icons-row">
                                        <img src={`${imgBasePath}/${systemCode}/img/share/aams.png`} alt="ADM" className="footer-icon-adm" crossOrigin="anonymous" onError={hideImg} />
                                        <img src={`${imgBasePath}/${systemCode}/img/share/timone.png`} alt="Timone" className="footer-icon-std" crossOrigin="anonymous" onError={hideImg} />
                                        <img src={`${imgBasePath}/${systemCode}/img/share/18.png`} alt="18+" className="footer-icon-small" crossOrigin="anonymous" onError={hideImg} />
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
});

export default MyBetsShareCard;